import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { CalendarClock, Save, Trash2, Plus, X } from 'lucide-react';
import { RangeForm, periodLabel } from './ClosuresCard';

const DAYS = [
  ['monday', 'Lun'], ['tuesday', 'Mar'], ['wednesday', 'Mer'],
  ['thursday', 'Gio'], ['friday', 'Ven'], ['saturday', 'Sab'], ['sunday', 'Dom'],
];

/** Turni settimanali e assenze di ogni operatore: bloccano le prenotazioni. */
export const StaffShiftsCard = () => {
  const { API, getAuthHeaders } = useAuth();
  const [staffList, setStaffList] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [shifts, setShifts] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/staff`, getAuthHeaders());
      setStaffList(res.data);
      setSelectedId(prev => prev || res.data[0]?.id || '');
    } catch {
      setStaffList([]);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const selected = staffList.find(s => s.id === selectedId);

  useEffect(() => {
    const base = {};
    DAYS.forEach(([key]) => {
      const saved = (selected?.shifts || {})[key];
      base[key] = saved || { closed: false, open: '09:00', close: '19:00', break_start: null, break_end: null };
    });
    setShifts(base);
  }, [selectedId, selected]);

  const setDay = (day, patch) => setShifts(prev => ({ ...prev, [day]: { ...prev[day], ...patch } }));

  const saveShifts = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/staff/${selectedId}/shifts`, { shifts }, getAuthHeaders());
      toast.success(`Turni di ${selected?.name} salvati`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio non riuscito');
    } finally {
      setSaving(false);
    }
  };

  const addAbsence = async (payload) => {
    try {
      await axios.post(`${API}/staff/${selectedId}/absences`, payload, getAuthHeaders());
      toast.success('Assenza registrata');
      load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Non salvata');
      return false;
    }
  };

  const removeAbsence = async (absenceId) => {
    try {
      await axios.delete(`${API}/staff/${selectedId}/absences/${absenceId}`, getAuthHeaders());
      load();
    } catch {
      toast.error('Eliminazione non riuscita');
    }
  };

  if (!staffList.length) return null;

  return (
    <Card data-testid="staff-shifts-card">
      <CardHeader>
        <CardTitle className="font-outfit flex items-center gap-2">
          <CalendarClock className="w-5 h-5 text-primary" />
          Turni e assenze
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Chi è fuori turno o assente non riceve appuntamenti, né online né a mano.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap gap-2" data-testid="staff-shifts-selector">
          {staffList.map((member) => (
            <button
              key={member.id}
              type="button"
              onClick={() => setSelectedId(member.id)}
              className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${
                selectedId === member.id
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`staff-shifts-pick-${member.id}`}
            >
              {member.name}
            </button>
          ))}
        </div>

        <div className="space-y-2">
          {DAYS.map(([key, label]) => shifts[key] && (
            <div key={key} className="flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2.5"
              data-testid={`staff-shift-row-${key}`}>
              <span className="w-12 shrink-0 text-sm font-medium">{label}</span>
              <Switch
                checked={!shifts[key].closed}
                onCheckedChange={(v) => setDay(key, { closed: !v })}
                data-testid={`staff-shift-active-${key}`}
              />
              {shifts[key].closed ? (
                <span className="text-sm text-muted-foreground">Riposo</span>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <Input type="time" value={shifts[key].open} className="h-9 w-[8.25rem]"
                    onChange={(e) => setDay(key, { open: e.target.value })}
                    data-testid={`staff-shift-from-${key}`} />
                  <span className="text-muted-foreground">–</span>
                  <Input type="time" value={shifts[key].close} className="h-9 w-[8.25rem]"
                    onChange={(e) => setDay(key, { close: e.target.value })}
                    data-testid={`staff-shift-to-${key}`} />
                  {(shifts[key].break_start || shifts[key].break_end) ? (
                    <>
                      <span className="text-xs text-muted-foreground">pausa</span>
                      <Input type="time" value={shifts[key].break_start || ''} className="h-9 w-[8.25rem]"
                        onChange={(e) => setDay(key, { break_start: e.target.value })}
                        data-testid={`staff-shift-break-from-${key}`} />
                      <span className="text-muted-foreground">–</span>
                      <Input type="time" value={shifts[key].break_end || ''} className="h-9 w-[8.25rem]"
                        onChange={(e) => setDay(key, { break_end: e.target.value })}
                        data-testid={`staff-shift-break-to-${key}`} />
                      <button type="button" aria-label="Togli la pausa"
                        onClick={() => setDay(key, { break_start: null, break_end: null })}
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        data-testid={`staff-shift-break-remove-${key}`}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </>
                  ) : (
                    <button type="button"
                      onClick={() => setDay(key, { break_start: '13:00', break_end: '14:00' })}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary transition-opacity hover:opacity-70"
                      data-testid={`staff-shift-break-add-${key}`}>
                      <Plus className="h-3.5 w-3.5" />
                      Pausa
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
          <div className="flex justify-end pt-1">
            <Button onClick={saveShifts} disabled={saving} data-testid="staff-shifts-save">
              <Save className="mr-2 h-4 w-4" />
              Salva turni
            </Button>
          </div>
        </div>

        <div className="space-y-3 border-t pt-4">
          <h3 className="text-base font-semibold md:text-lg">Assenze di {selected?.name}</h3>
          {(selected?.absences || []).length > 0 ? (
            <div className="space-y-2">
              {selected.absences.map((a) => (
                <div key={a.id} className="flex items-center gap-3 rounded-lg border px-3 py-2"
                  data-testid={`absence-${a.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{periodLabel(a)}</p>
                    {a.reason && <p className="truncate text-xs text-muted-foreground">{a.reason}</p>}
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeAbsence(a.id)}
                    data-testid={`absence-delete-${a.id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="absences-empty">
              Nessuna assenza registrata.
            </p>
          )}
          <RangeForm onSave={addAbsence} testPrefix="absence-new" />
        </div>
      </CardContent>
    </Card>
  );
};
